import { getAddressValidations } from "features/contracting/components/address/addressSubFormValidation";
import { getPhoneInputPrefixtDefault } from "features/shared/forms/components/phoneInput/phoneInputConfig";
import {
  FormValidations,
  SetValidationRuleFunction,
  ValidationActionType,
} from "features/shared/forms/hooks/useForm";
import FunctionValidationRule from "features/validation/rules/FunctionValidationRule";
import PhoneNumberValidatonRule from "features/validation/rules/PhoneNumberValidatonRule";
import ValidationRule from "features/validation/rules/ValidationRule";
import ValidationService from "features/validation/ValidationService";
import { authorizedAgentKey, mainAddressKey } from "./customerDataFormHelper";
import { CustomerType } from "features/customer/enums/customerType";
import PatternValidatonRule from "features/validation/rules/PatternValidatonRule";

export const customerDataFormValidations = (
  customerType: CustomerType,
  phonePrefixRef: React.RefObject<HTMLSelectElement>,
  authorizedAgentphonePrefixEl: React.RefObject<HTMLSelectElement>
): FormValidations => {
  const basicValidations = getBasicValidations(phonePrefixRef);
  const addressValidations = getAddressValidations(mainAddressKey);
  let customerTypeValidations: FormValidations = {};

  switch (customerType) {
    case "Person":
      customerTypeValidations = getHouseholdValidations();
      break;
    case "Company":
      customerTypeValidations = {
        ...getBusinessValidations(),
        ...getBusinessPersonValidations(
          authorizedAgentKey,
          authorizedAgentphonePrefixEl
        ),
      };
      break;
    default:
      break;
  }

  return {
    ...basicValidations,
    ...customerTypeValidations,
    ...addressValidations,
  };
};

const getBasicValidations = (
  phonePrefixRef: React.RefObject<HTMLSelectElement>
): FormValidations => {
  return {
    email: {
      required: true,
      type: "Function",
      validate: (value: any) => new ValidationService().ValidateEmail(value),
      message: "invalidEmail",
    } as FunctionValidationRule,
    mobileNumber: {
      required: true,
      type: "PhoneNumber",
      prefix: phonePrefixRef.current
        ? phonePrefixRef.current.value
        : getPhoneInputPrefixtDefault(),
      message: "invalidMobile",
    } as PhoneNumberValidatonRule,
    mailMethodCode: {
      required: true,
      type: "OnlyRequired",
    },
    dataBox: {
      type: "Pattern",
      regex: /^[abcdefghijkmnpqrstuvwxyz23456789]{7}$/,
      enableEmptyValue: true,
      message: "invalidDataBox",
    } as PatternValidatonRule
  };
};

const getHouseholdValidations = (): FormValidations => {
  return {
    firstName: {
      required: true,
      type: "OnlyRequired",
    },
    surname: {
      required: true,
      type: "OnlyRequired",
    },
    dateOfBirth: {
      required: true,
      type: "OnlyRequired",
    },
  };
};

const getBusinessValidations = (): FormValidations => {
  return {
    companyRegistrationNumber: {
      required: true,
      type: "Function",
      validate: (value: any) =>
        new ValidationService().validateCompanyRegistrationNumber(value),
      message: "invalidCompanyRegistrationNumber",
    } as FunctionValidationRule,
    taxId: {
      required: false,
      type: "Function",
      validate: validateTaxId,
      message: "invalidTaxId",
    } as FunctionValidationRule,
    businessName: {
      required: true,
      type: "OnlyRequired",
    },
  };
};

export const getBusinessPersonValidations = (
  objectPrefix: string,
  phonePrefixRef: React.RefObject<HTMLSelectElement>
): FormValidations => {
  let validations: FormValidations = {};

  validations[`${objectPrefix}.firstName`] = {
    required: true,
    type: "OnlyRequired",
  };

  validations[`${objectPrefix}.surname`] = {
    required: true,
    type: "OnlyRequired",
  };

  validations[`${objectPrefix}.email`] = {
    required: true,
    type: "Function",
    validate: (value: any) => new ValidationService().ValidateEmail(value),
    message: "invalidEmail",
  } as FunctionValidationRule;

  validations[`${objectPrefix}.mobileNumber`] = {
    required: true,
    type: "PhoneNumber",
    prefix: phonePrefixRef.current
      ? phonePrefixRef.current.value
      : getPhoneInputPrefixtDefault(),
    message: "invalidMobile",
  } as PhoneNumberValidatonRule;

  return validations;
};

export const setBusinessPersonValidations = (
  objectPrefix: string,
  phonePrefixRef: React.RefObject<HTMLSelectElement>,
  setValidationRule: SetValidationRuleFunction,
  action: ValidationActionType = "add"
) => {
  const validations = getBusinessPersonValidations(
    objectPrefix,
    phonePrefixRef
  );

  for (const [key, rule] of Object.entries(validations)) {
    setValidationRule(key, rule, action);
  }
};

export const setMailMethodEmailsValidation = (
  setValidationRule: SetValidationRuleFunction,
  action: ValidationActionType = "add"
) => {
  const rule: ValidationRule = {
    required: true,
    type: "OnlyRequired",
  };

  setValidationRule("mailMethodEmails", rule, action);
};

const validateTaxId = (value: string) => {
  if (value) {
    const validationService = new ValidationService();
    return validationService.validateTaxId(value);
  }

  return true;  
}